/**
 * @fileoverview Boolean entry query — the shared "which entries are
 * included" filter behind widgets and pivot reports (MOR-906 §7.8).
 *
 * Purpose:
 * - Represent a bracketed AND/OR/NOT expression over time entry dimensions
 * - Validate it, compile it, and evaluate it against a single entry
 *
 * The token shape itself lives in convex/timesheets/savedTimeReports/schema.ts
 * (`timeQueryTokenValidator`) because saved widgets and reports persist it.
 *
 * NOT is folded into the condition (`negate`) rather than being its own
 * operator: every dimension on a time entry is single-valued, so the only
 * two questions worth asking are "is any of" and "is none of".
 *
 * Pure module — no Convex context, fully unit tested.
 *
 * Used by:
 * - convex/timesheets/reportBuilder.ts (runReport)
 * - convex/timesheets/pivot.ts (runPivot)
 * - src/components/timesheets/EntryQueryBuilder.tsx
 */

import type { Doc } from "@cvx/_generated/dataModel";
import type {
  TimeQueryField,
  TimeQueryToken,
} from "@cvx/timesheets/savedTimeReports/schema";

export type QueryField = TimeQueryField;
export type QueryToken = TimeQueryToken;

/**
 * Grade shown for time approved without a grade on the rate snapshot —
 * matches the REM pack's fallback so the two surfaces agree.
 */
export const UNGRADED = "Ungraded";

/** The dimension values of one entry, as the strings conditions compare to. */
export interface QueryEntryFields {
  user: string;
  job: string;
  category: string;
  billable: string;
  status: string;
  grade: string;
  writtenOff: string;
}

/**
 * Project a time entry onto the comparable dimension values.
 *
 * @param categoryKeyById - category id → stable key, so a renamed category
 * keeps matching.
 */
export function entryQueryFields(
  entry: Doc<"timeEntries">,
  categoryKeyById: ReadonlyMap<string, string>,
): QueryEntryFields {
  return {
    user: entry.userId as string,
    // Firm-overhead entries carry no matter — an empty value never matches a
    // "matter is any of …" condition, only an explicit "matter is none of".
    job: (entry.jobId as string | undefined) ?? "",
    category: categoryKeyById.get(entry.categoryId as string) ?? "",
    billable: entry.billable ? "true" : "false",
    status: entry.status,
    grade: entry.rateSnapshot?.gradeName ?? UNGRADED,
    writtenOff: entry.writtenOffAt ? "true" : "false",
  };
}

/** Bounds — a filter is a UI-composed expression, not a program. */
const MAX_TOKENS = 60;
const MAX_VALUES_PER_CONDITION = 200;

/**
 * Check a token array is a well-formed expression.
 *
 * @returns null when valid, otherwise a message written for the person who
 * built the expression (it surfaces directly in the builder).
 */
export function validateTokens(tokens: readonly QueryToken[]): string | null {
  if (tokens.length === 0) return null; // No filter is a valid filter.
  if (tokens.length > MAX_TOKENS) {
    return `Filter is too long — keep it under ${MAX_TOKENS} parts`;
  }

  let depth = 0;
  // What may legally come next: an operand (condition or "(") or an
  // operator (AND/OR or ")"). The expression starts expecting an operand.
  let expectOperand = true;

  for (const token of tokens) {
    switch (token.t) {
      case "open":
        if (!expectOperand) return "Add AND or OR before the bracket";
        depth += 1;
        break;
      case "close":
        if (expectOperand) return "Empty or unfinished bracket";
        if (depth === 0) return "Unmatched closing bracket";
        depth -= 1;
        break;
      case "and":
      case "or":
        if (expectOperand) {
          return `${token.t.toUpperCase()} needs a condition on both sides`;
        }
        expectOperand = true;
        break;
      case "cond": {
        if (!expectOperand) return "Add AND or OR between conditions";
        if (token.values.length === 0) {
          return "Pick at least one value for every condition";
        }
        if (token.values.length > MAX_VALUES_PER_CONDITION) {
          return "That condition has too many values";
        }
        expectOperand = false;
        break;
      }
    }
  }

  if (expectOperand) return "Finish the filter with a condition";
  if (depth > 0) return "Close every bracket you open";
  return null;
}

/** AND binds tighter than OR, as everywhere else. */
const PRECEDENCE: Record<"and" | "or", number> = { and: 2, or: 1 };

/**
 * Shunting-yard: infix tokens → reverse Polish notation, so evaluation is a
 * single pass over a stack with no recursion.
 *
 * Assumes `validateTokens` has already passed; brackets are dropped.
 */
export function tokensToRpn(tokens: readonly QueryToken[]): QueryToken[] {
  const output: QueryToken[] = [];
  const operators: QueryToken[] = [];

  for (const token of tokens) {
    switch (token.t) {
      case "cond":
        output.push(token);
        break;
      case "and":
      case "or": {
        while (operators.length > 0) {
          const top = operators[operators.length - 1];
          if (top.t !== "and" && top.t !== "or") break;
          // Left-associative, so equal precedence pops too.
          if (PRECEDENCE[top.t] < PRECEDENCE[token.t]) break;
          output.push(operators.pop() as QueryToken);
        }
        operators.push(token);
        break;
      }
      case "open":
        operators.push(token);
        break;
      case "close": {
        while (operators.length > 0) {
          const top = operators.pop() as QueryToken;
          if (top.t === "open") break;
          output.push(top);
        }
        break;
      }
    }
  }
  while (operators.length > 0) {
    const top = operators.pop() as QueryToken;
    if (top.t === "open") continue;
    output.push(top);
  }
  return output;
}

/**
 * Evaluate a compiled expression against one entry.
 *
 * An empty expression matches everything — "no filter" must never exclude
 * anything, so widgets and reports without a filter behave as before.
 */
export function matchesQuery(
  fields: QueryEntryFields,
  rpn: readonly QueryToken[],
): boolean {
  if (rpn.length === 0) return true;

  const stack: boolean[] = [];
  for (const token of rpn) {
    if (token.t === "cond") {
      const hit = token.values.includes(fields[token.field]);
      stack.push(token.negate ? !hit : hit);
      continue;
    }
    if (token.t === "and" || token.t === "or") {
      const right = stack.pop();
      const left = stack.pop();
      // Malformed RPN (validation skipped) — fail open rather than throw
      // mid-aggregation.
      if (right === undefined || left === undefined) return true;
      stack.push(token.t === "and" ? left && right : left || right);
    }
  }
  return stack.pop() ?? true;
}

/**
 * Validate and compile in one step, for query handlers.
 *
 * @throws when the expression is malformed — a saved report holding a broken
 * filter should say so rather than quietly return every entry.
 */
export function compileEntryQuery(
  tokens: readonly QueryToken[] | undefined,
): QueryToken[] {
  const compiled = tryCompileEntryQuery(tokens);
  if (compiled.error) {
    throw new Error(`Filter is not valid: ${compiled.error}`);
  }
  return compiled.rpn;
}

/**
 * Same as `compileEntryQuery`, but an unfinished expression (the normal
 * state while someone is still clicking AND / OR / a condition) returns
 * the error instead of throwing. Runners use this so a mid-edit filter
 * never surfaces as a Convex exception.
 */
export function tryCompileEntryQuery(
  tokens: readonly QueryToken[] | undefined,
): { rpn: QueryToken[]; error: string | null } {
  if (!tokens || tokens.length === 0) return { rpn: [], error: null };
  const error = validateTokens(tokens);
  if (error) return { rpn: [], error };
  return { rpn: tokensToRpn(tokens), error: null };
}
